import React, { useEffect, useState } from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import classnames from 'classnames/bind';

import { notify } from '@NOTIFICATION/Notificator';

import { useUpdateVariant, useGetSectionByIdAdmin } from '@SERVICES';

import useEditVariantModal from '@HOOKS/store/modals/useEditVariantModal';

import BaseModal from '@MODALS/BaseModal';

import VariantForm, { VariantFormValues } from '@PAGES/admin/edit-section/components/VariantForm/VariantForm.component';

import { AdminSingleSection, AdminSingleSectionMeta } from '@INTERFACES/admin-api/sections';
import CircularProgressIndicator from '@COMPONENTS/SHARED/CircularProgressIndicator';
import COLOR from '@CONSTANTS/COLOR.constant';
import styles from './EditVariantModal.module.scss';

const cx: CX = classnames.bind(styles);

const defaultData: AdminSingleSection = {
    data: null,
    meta: null,
};

function ModalBody({
    isLoading,
    isError,
    canEditDocument,
    sectionMeta,
    documentId,
    chapterId,
    sectionId,
    variantId,
}: ModalBodyProps) {
    const [
        updateVariant,
        {
            isLoading: isUpdating, isSuccess: isUpdateSuccess, isError: isUpdateError, error: updateError,
        },
    ] = useUpdateVariant();

    const {
        actions: editVariantModalActions,
    } = useEditVariantModal();

    const {
        data: sectionData = defaultData, 
        isLoading: isSectionLoading, 
        isError: isSectionError,
    } = useGetSectionByIdAdmin(sectionId);

    // Find the variant being edited
    const variant = sectionData?.data?.variants?.find((v) => v.id === variantId);

    useEffect(() => {
        if (isUpdateSuccess) {
            notify.info('Variant has been updated', {
                toastId: 'update-variant',
            });

            editVariantModalActions.close();
        }
    }, [isUpdateSuccess, editVariantModalActions]);

    useEffect(() => {
        if (isUpdateError) {
            if ((updateError as FetchBaseQueryError).status === 403) {
                notify.error('Permission denied', {
                    toastId: 'update-variant-permission-error',
                });
            } else {
                notify.error('Something wrong!', {
                    toastId: 'update-variant-error',
                });
            }
        }
    }, [isUpdateError, updateError]);

    if (isLoading || isSectionLoading) {
        return (
            <div className={cx('loading')}>
                <CircularProgressIndicator
                    color={COLOR['marriott-primary']}
                    size={40}
                    thickness={4}
                />
            </div>
        );
    }

    if (isError || isSectionError || !variant) {
        return (
            <div className={cx('error')}>Something wrong!</div>
        );
    }

    return (
        <VariantForm
            locked={!canEditDocument}
            buttonTitle="Update"
            isLoading={isUpdating}
            defaultValues={{
                isDefault: variant.isDefault,
                brands: variant.brands || [],
                regions: variant.regions || [],
                areas: variant.areas || [],
                countries: variant.countries || [],
                propertyTypes: variant.propertyTypes || [],
            }}
            handleToggleDefault={() => {
                // For editing, we don't allow changing default status through the form
                // This is handled by the toggle in the main variant component
            }}
            sectionMeta={sectionMeta}
            newVariant={false}
            onSave={(formValues: VariantFormValues) => {
                const { isDefault } = variant;

                // When updating variant options, preserve the existing body content.
                // This is critical to avoid overwriting text content when only updating options.
                const preservedBody = variant.body || '';
                
                // Log warning if body is empty - this helps debug the issue
                if (!preservedBody) {
                    console.warn('⚠️ Variant body is empty when updating options. This may indicate text content was lost.', {
                        variantId,
                        variant,
                        sectionId
                    });
                }

                updateVariant({
                    documentId,
                    chapterId,
                    sectionId,
                    id: variantId,
                    name: variant.name || '',
                    isVisible: variant.isVisible,
                    body: preservedBody, // Always preserve existing body content
                    isDefault,
                    brands: isDefault ? [] : formValues.brands,
                    regions: isDefault ? [] : formValues.regions,
                    areas: isDefault ? [] : formValues.areas,
                    countries: isDefault ? [] : formValues.countries,
                    propertyTypes: isDefault ? [] : formValues.propertyTypes,
                });
            }}
        />
    );
}

interface ModalBodyProps {
    isLoading: boolean;
    isError: boolean;
    canEditDocument: boolean;
    sectionMeta: AdminSingleSectionMeta;
    documentId: number;
    chapterId: number;
    sectionId: number;
    variantId: number;
}

function EditVariantModal() {
    const {
        state: { data: modalParams },
        actions: editVariantModalActions,
    } = useEditVariantModal();

    const { sectionId, canEditDocument, variantId, documentId, chapterId } = modalParams!;

    const {
        data: sectionData = defaultData, isLoading: isSectionLoading, isError,
    } = useGetSectionByIdAdmin(sectionId);

    function handleClose() {
        editVariantModalActions.close();
    }

    return (
        <BaseModal
            onClose={() => handleClose()}
        >
            <div className={cx('edit-variant-modal')}>
                <div className={cx('title')}>
                    Edit variant applicability
                </div>

                <ModalBody
                    isLoading={isSectionLoading}
                    isError={isError}
                    canEditDocument={canEditDocument}
                    sectionMeta={sectionData.meta!}
                    documentId={documentId}
                    chapterId={chapterId}
                    sectionId={sectionId}
                    variantId={variantId}
                />
            </div>
        </BaseModal>
    );
}

export default EditVariantModal; 